import { useCallback, useEffect, useRef } from "react";
import {
  composerMarkdownToHtml as composerMdToHtml,
  htmlToMarkdown as sharedHtmlToMarkdown,
  cleanPastedHtml as sharedCleanPastedHtml,
  applyActiveFormatsIfChanged,
  readComposerFormats,
} from "../utils/composerUtils";

export function useRichEditor(isComposerToolbarOpen, setMessage, setActiveFormats) {
  const richEditorRef = useRef(null);
  const pendingPasteHtmlRef = useRef(null);
  const pendingToggleHtmlRef = useRef(null);
  const activeFormatsRef = useRef(null);

  const composerMarkdownToHtml = useCallback((md) => composerMdToHtml(md), []);
  const htmlToMarkdown = useCallback((html) => sharedHtmlToMarkdown(html), []);
  const cleanPastedHtml = useCallback((html) => sharedCleanPastedHtml(html), []);

  const updateActiveFormats = useCallback(() => {
    if (!isComposerToolbarOpen) return;
    try {
      applyActiveFormatsIfChanged(setActiveFormats, activeFormatsRef, readComposerFormats(richEditorRef.current));
    } catch (e) { /* ignore */ }
  }, [isComposerToolbarOpen, setActiveFormats]);

  useEffect(() => {
    if (!isComposerToolbarOpen) return;
    document.addEventListener("selectionchange", updateActiveFormats);
    return () => document.removeEventListener("selectionchange", updateActiveFormats);
  }, [isComposerToolbarOpen, updateActiveFormats]);

  const getRichEditorContent = useCallback(() => {
    const editor = richEditorRef.current;
    if (!editor) return "";
    return sharedHtmlToMarkdown(editor.innerHTML);
  }, []);

  useEffect(() => {
    if (isComposerToolbarOpen && pendingPasteHtmlRef.current && richEditorRef.current) {
      richEditorRef.current.innerHTML = sharedCleanPastedHtml(pendingPasteHtmlRef.current);
      const md = sharedHtmlToMarkdown(richEditorRef.current.innerHTML);
      setMessage(md);
      pendingPasteHtmlRef.current = null;
      const range = document.createRange();
      range.selectNodeContents(richEditorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else if (isComposerToolbarOpen && pendingToggleHtmlRef.current !== null && richEditorRef.current) {
      richEditorRef.current.innerHTML = pendingToggleHtmlRef.current;
      pendingToggleHtmlRef.current = null;
      const range = document.createRange();
      range.selectNodeContents(richEditorRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isComposerToolbarOpen, setMessage]);

  return {
    richEditorRef,
    pendingPasteHtmlRef,
    pendingToggleHtmlRef,
    composerMarkdownToHtml,
    htmlToMarkdown,
    cleanPastedHtml,
    updateActiveFormats,
    getRichEditorContent,
  };
}
